import { useEffect, useState } from 'react'
import { useIsGuest } from 'hooks/user'
import { useApplyToJob } from 'hooks/job'
import { graphql } from 'react-relay'
import { trackError, trackEvent } from 'services/analytics'
import { useRouter } from 'next/router'
import { useFragment } from 'react-relay/hooks'
import { useJobApplication_User$key } from '__generated__/useJobApplication_User.graphql'
import { useJobApplication_Job$key } from '__generated__/useJobApplication_Job.graphql'
import { useJobApplication_JobApplication$key } from '__generated__/useJobApplication_JobApplication.graphql'

const STEPS = ['confirmingContract', 'confirmingJobSpec', 'sent'] as const

export function useJobApplication(isApplying: boolean, jobFragmentRef: useJobApplication_Job$key, userFragmentRef: useJobApplication_User$key) {
  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // the way this hook works is simple:
  // there is a set of steps, and changing between steps may or may not trigger a side effect
  // for example, reaching the last step causes the job application to be sent as a side effect
  // additionally, some steps are skipped, such as the contract confirmation for when the user does not have a contract yet
  // skipping is again done as a side-effect
  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

  const [step, setStep] = useState(0)
  const router = useRouter()

  const job = useFragment(graphql`
    fragment useJobApplication_Job on Job {
      slug

      firm {
        slug
      }
      
      jobApplication {
        ...useJobApplication_JobApplication
      }
      
      ...job_useApplyToJob
    }
  `, jobFragmentRef)

  const jobApplication = useFragment(graphql`
    fragment useJobApplication_JobApplication on JobApplication {
      profileWillUpdateOnApply
      profileCompatibleWithJob
    }
  `, job?.jobApplication as useJobApplication_JobApplication$key)

  const user = useFragment(graphql`
    fragment useJobApplication_User on User {
      status
      workingOrOfferedJob
      
      ...user_useIsGuest
    }
  `, userFragmentRef)

  // derived data
  const isGuest = useIsGuest(user)
  const stepName = STEPS[step]
  const userHasContract = user?.workingOrOfferedJob || false
  const shouldRedirectUser = isApplying && job?.slug && (isGuest || user?.status === 'pending')

  // NOTE: if there is no `job.contract` then needsConfirmationStep is TRUE. This is correct because the
  // JobSpecConfirmationDialog will automatically cause the contract to be created and then this component
  // will re-render with the appropriate needsConfirmationStep value.
  // TODO: refactor the code so this flow is more obvious
  const needsConfirmationStep = jobApplication ? (jobApplication?.profileWillUpdateOnApply || !jobApplication?.profileCompatibleWithJob) : true

  const { apply } = useApplyToJob(job)

  // alternative behavior for guests or users that didn't submit a profile, with redirection
  useEffect(() => {
    if (shouldRedirectUser) {
      if (isGuest) {
        router.push(`/signup/member?job=${job?.slug}&firm=${job?.firm?.slug}`)
      } else if (user?.status === 'pending') {
        router.push(`/profile?job=${job?.slug}&firm=${job?.firm?.slug}`)
      }
    }
  }, [shouldRedirectUser, isGuest, user?.status])

  // submit final or draft application
  // these run even when the steps are skipped as a side effect
  useEffect(() => {
    if (isApplying) {
      if (stepName === 'confirmingJobSpec') {
        apply('job_application_draft').catch(err => trackError(err))
      } else if (stepName === 'sent') {
        trackEvent('Job Spec Confirmed') // TODO: would be cool to detect if user accepted to lower their rate.
        apply('job_application_sent').catch(err => trackError(err))
      }
    }
  }, [stepName, isApplying])

  // track confirmation step view
  useEffect(() => {
    if (stepName === 'confirmingJobSpec' && needsConfirmationStep) {
      trackEvent('Job Spec Confirmation Dialog Opened')
    }
  }, [stepName, needsConfirmationStep])

  // skip 'confirmingContract' step if unnecessary
  useEffect(() => {
    if (stepName === 'confirmingContract' && !userHasContract) {
      setStep(step + 1)
    }
  }, [step, stepName, userHasContract])

  // skip 'confirmingJobSpec' step if unnecessary
  useEffect(() => {
    if (stepName === 'confirmingJobSpec' && !needsConfirmationStep) {
      setStep(step + 1)
    }
  }, [step, stepName, needsConfirmationStep])

  return {
    step: STEPS[step],
    next: () => setStep(Math.min(step + 1, STEPS.length - 1)),
    redirecting: shouldRedirectUser,
  }
}
